import React, {useEffect, useRef} from "react";
import styles from "./styles.module.scss";
import classNames from "classnames";
import {TextMedium14} from "@/app/components/texts";


interface TipProps {
  onChange?: () => void;
  tip?: React.ReactNode;
  isSelected?: boolean;
}

const Tip: React.FC<TipProps> = ({
                                   onChange = () => {
                                   },
                                   tip,
                                   isSelected
                                 }) => {
  return (
    <button className={classNames(styles.tip, {[styles.selected]: isSelected})} onClick={onChange}>{tip}</button>
  );
};

interface TipsProps<T extends string> {
  onChange?: (selected: T[]) => void;
  tips?: T[];
  className?: string;
  initialValues?: T[];
}

export function Tips<T extends string>({
                                         onChange = () => {
                                         },
                                         tips,
                                         className,
                                         initialValues = []
                                       }: TipsProps<T>) {
  const [selected, setSelected] = React.useState<T[]>([]);
  const prevSelectedRef = useRef<T[]>([]);
  const isInitializedRef = useRef(false);
  const prevInitialValuesRef = useRef<T[]>([]);

  // Update selected state when initialValues changes, but only if they're different
  useEffect(() => {
    // Skip if initialValues haven't changed
    if (areArraysEqual(prevInitialValuesRef.current, initialValues)) {
      return;
    }
    
    prevInitialValuesRef.current = [...initialValues];
    
    if (initialValues && Array.isArray(initialValues) && initialValues.length > 0) {
      // Only update if the values are different
      if (!areArraysEqual(selected, initialValues)) {
        setSelected(initialValues);
      }
    }
  }, [initialValues, selected]);

  // Helper function to compare arrays
  function areArraysEqual(arr1: T[], arr2: T[]): boolean {
    if (arr1.length !== arr2.length) return false;
    return arr1.every((item, index) => item === arr2[index]);
  }

  function toggleToSelected(tip: T) {
    if (selected.includes(tip)) {
      setSelected(selected.filter((s) => s !== tip));
    } else {
      setSelected([...selected, tip]);
    }
  }

  // Call onChange only when selected actually changes and is different from initialValues
  useEffect(() => {
    // Skip the first render
    if (!isInitializedRef.current) {
      isInitializedRef.current = true;
      prevSelectedRef.current = [...selected];
      return;
    }
    
    // Skip if selected hasn't changed
    if (areArraysEqual(prevSelectedRef.current, selected)) {
      return;
    }
    
    // Skip if selected is the same as initialValues
    if (areArraysEqual(selected, initialValues)) {
      return;
    }
    
    prevSelectedRef.current = [...selected];
    onChange(selected);
  }, [selected, onChange, initialValues]);

  return (
    <div className={classNames(styles.tips, className)}>
      {tips?.map((tip, index) => (
        <Tip key={index} tip={<TextMedium14 text={tip}/>} onChange={() => toggleToSelected(tip)}
             isSelected={selected.includes(tip)}/>
      ))}
    </div>
  )
}